import { Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { stateAria } from "../a11y";

// THE pager nav row — ‹ › arrows, tappable position dots and a mono text
// counter (so the position is never colour-only). Extracted from
// ProgramHighlightCarousel when the PR badge gallery became the 2nd pager
// (HR-9b). Presentational and i18n-agnostic: the caller passes
// already-translated a11y labels. Renders nothing for a single page.
// Arrows are 44pt boxes and the primary navigation on web, where a
// hidden-scrollbar pager can't be mouse-dragged; the tiny dots reach the
// 44pt floor via padding + hitSlop, never a bigger visual box.
export function PagerNav({
  theme,
  index,
  count,
  onGoTo,
  prevLabel,
  nextLabel,
  positionLabel,
  dotLabel,
  counterText,
}: {
  theme: UiTheme;
  index: number;
  count: number;
  onGoTo: (i: number) => void;
  /** Already-translated accessibility labels (caller owns i18n). */
  prevLabel: string;
  nextLabel: string;
  positionLabel: string;
  /** Per-dot label ("Record 2 of 5") — a bare selected/unselected button
   *  tells a screen reader nothing about where it jumps. */
  dotLabel?: (i: number) => string;
  /**
   * Overrides the visible counter, which defaults to bare "2/4". PE-27 needed
   * "W2/4": on a surface that also carries a day×week grid, two bare digits are
   * a coordinate with no axis, and the reader has to guess whether it counts
   * days or weeks. The spoken label already said which; the visible one has to
   * as well, because that is the one everybody reads.
   */
  counterText?: string;
}) {
  const styles = makeStyles(theme);
  if (count <= 1) return null;

  return (
    <View style={styles.nav}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={prevLabel}
        accessibilityState={{ disabled: index === 0 }}
        {...stateAria({ disabled: index === 0 })}
        disabled={index === 0}
        onPress={() => onGoTo(index - 1)}
        style={({ pressed }) => [styles.arrow, pressed && styles.arrowPressed]}
      >
        <Text style={[styles.arrowText, index === 0 && styles.arrowDisabled]}>
          ‹
        </Text>
      </Pressable>

      <View
        style={styles.dots}
        accessibilityRole="text"
        accessibilityLabel={positionLabel}
      >
        {Array.from({ length: count }, (_, i) => (
          <Pressable
            key={i}
            accessibilityRole="button"
            accessibilityLabel={dotLabel?.(i)}
            accessibilityState={{ selected: i === index }}
            {...stateAria({ selected: i === index }, "button")}
            onPress={() => onGoTo(i)}
            hitSlop={14}
            style={styles.dotTap}
          >
            <View style={[styles.dot, i === index && styles.dotActive]} />
          </Pressable>
        ))}
        {/* Text counter so the position is never colour-only (a11y). */}
        <Text style={styles.counter}>
          {counterText ?? `${index + 1}/${count}`}
        </Text>
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={nextLabel}
        accessibilityState={{ disabled: index === count - 1 }}
        {...stateAria({ disabled: index === count - 1 })}
        disabled={index === count - 1}
        onPress={() => onGoTo(index + 1)}
        style={({ pressed }) => [styles.arrow, pressed && styles.arrowPressed]}
      >
        <Text
          style={[
            styles.arrowText,
            index === count - 1 && styles.arrowDisabled,
          ]}
        >
          ›
        </Text>
      </Pressable>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    nav: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    arrow: {
      minWidth: 44,
      minHeight: 44,
      alignItems: "center",
      justifyContent: "center",
    },
    arrowPressed: { opacity: 0.5 },
    arrowText: { color: p.ink2, fontSize: t.sizes.xl, fontWeight: "700" },
    arrowDisabled: { color: p.ink3, opacity: 0.4 },
    dots: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: s.s2,
    },
    // 8×8 visual dot + padding + hitSlop 14 = a 44×44 touch box (the a11y
    // floor) while the dot itself stays tiny.
    dotTap: { paddingVertical: s.s2, paddingHorizontal: s.s2 },
    dot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: p.border,
    },
    // Ink ring on the active dot: brand500 flat fill is under 3:1 against
    // surface/bg on brand-light and rosepine-light, so the "you are here"
    // cue gets a guaranteed-contrast outline (and a shape cue, not colour
    // alone).
    dotActive: {
      backgroundColor: p.brand500,
      borderWidth: 1.5,
      borderColor: p.ink,
    },
    // ink, not ink2 — 12px is small text and ink2 misses the 4.5 floor on
    // catppuccin-light (the default theme), on both current mounts.
    counter: {
      marginLeft: s.s2,
      color: p.ink,
      fontSize: t.sizes.sm,
      fontFamily: t.fonts.mono,
    },
  });
}
